import { ScreenHeader, Skeleton } from "../components/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type EarningsParams,
  type EarningsSeries,
  type HistorySeries,
  fetchEarningsSeries,
  fetchHistorySeries,
} from "../api";
import { formatEur } from "../format";
import { PeriodPicker } from "../components/PeriodPicker";

/**
 * History tab: energy first, money as context (the Prijzen tab is
 * money first). PeriodPicker, kWh KPI tiles + self-sufficiency, the
 * EUR strip and the Saldo-per-bucket chart (only with a `pricing:`
 * config), three paired bar charts, and Inkomsten/Uitgaven from
 * /api/earnings/series so the cents match the Prijzen tab. The
 * earnings fetch may fail silently; the energy section still renders.
 * docs/customer-app.md > Front-end > History tab
 */

const SUMMARY_POLL_MS = 60_000;   // bucket-totals change slowly

export function History() {
  const [params, setParams] = useState<EarningsParams>({ range: "last_7d" });
  const [data, setData] = useState<HistorySeries | null>(null);
  const [earnings, setEarnings] = useState<EarningsSeries | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  // Stable key so a fresh `{range:'today'}` object doesn't loop the
  // effect on every render. Same trick as EarningsHistory.
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    async function poll() {
      const my = ++seq.current;
      setLoading(true);
      // Parallel: energy is the primary load, earnings is the side-car.
      // `allSettled` so a config-fetch hiccup doesn't blank the page -
      // the energy section is useful on its own.
      const [hRes, eRes] = await Promise.allSettled([
        fetchHistorySeries(params),
        fetchEarningsSeries(params),
      ]);
      if (cancelled || my !== seq.current) return;
      if (hRes.status === "fulfilled") {
        setData(hRes.value);
        setError(null);
      } else {
        setError(hRes.reason instanceof Error ? hRes.reason.message : String(hRes.reason));
      }
      if (eRes.status === "fulfilled") {
        setEarnings(eRes.value);
      } else {
        // Earnings is non-fatal - site might have no pricing config.
        setEarnings(null);
      }
      setLoading(false);
    }
    poll();
    // Long poll - bucket totals don't shift second-to-second. Live
    // power lives in /live; this tab is for the slower picture.
    timer = window.setInterval(poll, SUMMARY_POLL_MS);

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Range totals - what the KPI tiles show.
  const totals = useMemo(() => {
    const sum = (a?: number[]) => (a ? a.reduce((s, v) => s + v, 0) : 0);
    return {
      pv: sum(data?.pv_kwh),
      load: sum(data?.load_kwh),
      grid_in: sum(data?.grid_in_kwh),
      grid_out: sum(data?.grid_out_kwh),
      to_batt: sum(data?.to_battery_kwh),
      from_batt: sum(data?.from_battery_kwh),
    };
  }, [data]);

  // Zelfvoorziening komt van de backend, PER UUR berekend: de oude
  // range-formule (1 - import/verbruik) klapte naar 0% zodra de accu
  // goedkope-stroom-arbitrage doet (import >> huisverbruik).
  const selfSuff = data?.self_sufficiency_pct ?? null;

  // Earnings totals for the strip + Inkomsten/Uitgaven chart. Reuses
  // the existing /api/earnings/series - same numbers as the Prijzen
  // tab for the same range. `contract === null` means the site has no
  // pricing config; hide the whole money section.
  const eurTotals = useMemo(() => {
    if (!earnings || earnings.contract == null) return null;
    const sum = (a: number[]) => a.reduce((s, v) => s + v, 0);
    const earned = sum(earnings.eur_earned);
    const spent  = sum(earnings.eur_spent);
    return { earned, spent, net: earned - spent };
  }, [earnings]);

  return (
    <div className="tab-enter stagger px-4 pt-6 pb-28 max-w-3xl mx-auto flex flex-col gap-6 lg:max-w-5xl lg:px-8 lg:pt-10 lg:pb-12">
      <header className="flex flex-col gap-3">
        <ScreenHeader title="Historie" subtitle="Energie & opbrengst terugkijken" />
        <PeriodPicker value={params} onChange={setParams} />
      </header>

      {error && (
        <div className="text-sm text-danger bg-danger-soft rounded-xl p-3 border border-card-border">
          {error}
        </div>
      )}

      {data == null ? (
        <Skeleton className="h-32" />
      ) : (
        <div className={loading ? "opacity-70 transition-opacity" : "transition-opacity"}>
          {/* KPI tiles - money-tab parallel: totals first, charts below. */}
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label="Opgewekt"    value={totals.pv}       hint="zonproductie" tone="accent" />
            <Kpi label="Verbruikt"   value={totals.load}     hint="huishouden" />
            <Kpi label="Geïmporteerd" value={totals.grid_in}  hint="van het net" tone="cost" />
            <Kpi label="Geëxporteerd" value={totals.grid_out} hint="naar het net" tone="accent" />
          </section>

          {/* Self-sufficiency strip - per uur gemeten, dus ook
              betekenisvol op een accu die voor arbitrage importeert. */}
          {selfSuff != null && (
            <div className="card p-4 mt-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-ink-mute">Zelfvoorzienend</span>
                <span className="text-xl font-semibold tabular text-accent">
                  {selfSuff.toFixed(0)}%
                </span>
              </div>
              <div className="text-xs text-ink-mute mt-1">
                deel van het huisverbruik dat niet op dat moment van het
                net kwam (directe zon of accu), per uur gemeten
              </div>
            </div>
          )}

          {/* Saldo strip - magnitude-first headline (same convention
              as HomeEarnings / EarningsHistory: "€ 2,69 kosten" reads
              as the bill, "€ -2,69" reads weird). Hidden when there's
              no contract - site without pricing config shouldn't see
              €0,00 (looks broken). */}
          {eurTotals && (
            <div className="card p-4 mt-4 grid grid-cols-3 gap-3">
              <div>
                <div className="text-xs text-ink-mute">Saldo</div>
                <div
                  className={`text-xl font-semibold mt-0.5 tabular ${
                    eurTotals.net >= 0 ? "text-accent" : "text-ink"
                  }`}
                >
                  {formatEur(Math.abs(eurTotals.net))}
                </div>
                <div className="text-xs text-ink-mute">
                  {eurTotals.net >= 0 ? "winst" : "kosten"}
                </div>
              </div>
              <div>
                <div className="text-xs text-ink-mute">Verdiend</div>
                <div className="text-base font-medium mt-0.5 tabular">
                  {formatEur(eurTotals.earned)}
                </div>
                <div className="text-xs text-ink-mute">teruggeleverd</div>
              </div>
              <div>
                <div className="text-xs text-ink-mute">Uitgegeven</div>
                <div className="text-base font-medium mt-0.5 tabular">
                  {formatEur(eurTotals.spent)}
                </div>
                <div className="text-xs text-ink-mute">afgenomen</div>
              </div>
            </div>
          )}

          {/* Three paired bar charts. Each shows two opposing series so
              the "balance" reads at a glance - e.g. tall green PV with
              a short orange Load means a big export day. */}
          <section className="flex flex-col gap-4 mt-6">
            {data.t.length === 0 ? (
              <div className="text-sm text-ink-mute card p-4">
                Nog geen data voor deze periode.
              </div>
            ) : (
              <>
                {/* Saldo EERST - het antwoord waar de klant voor
                    komt: wat leverde elke dag op? De prijslijn die
                    hier stond dupliceerde de Prijzen-tab en zei
                    niets over dit huishouden (10-09). */}
                {earnings && earnings.contract != null &&
                  earnings.t.length > 0 && (
                    <SaldoChart
                      t={earnings.t}
                      bucket={earnings.bucket}
                      earned={earnings.eur_earned}
                      spent={earnings.eur_spent}
                    />
                )}
                <PairChart
                  title="Opwekking & verbruik"
                  t={data.t}
                  bucket={data.bucket}
                  aLabel="Opgewekt"
                  aValues={data.pv_kwh}
                  aColor="var(--color-accent, #16a34a)"
                  bLabel="Verbruikt"
                  bValues={data.load_kwh}
                  bColor="var(--color-price-high, #ea580c)"
                />
                <PairChart
                  title="Net uitwisseling"
                  t={data.t}
                  bucket={data.bucket}
                  aLabel="Geïmporteerd"
                  aValues={data.grid_in_kwh}
                  aColor="var(--color-price-high, #ea580c)"
                  bLabel="Geëxporteerd"
                  bValues={data.grid_out_kwh}
                  bColor="var(--color-accent, #16a34a)"
                />
                <PairChart
                  title="Batterijgebruik"
                  t={data.t}
                  bucket={data.bucket}
                  aLabel="Geladen"
                  aValues={data.to_battery_kwh}
                  aColor="var(--color-batt-charge, #38bdf8)"
                  bLabel="Ontladen"
                  bValues={data.from_battery_kwh}
                  bColor="var(--color-batt-discharge, #f59e0b)"
                />
                {/* €-chart only when we have a contract. Numbers match
                    EarningsHistory on the Prijzen tab (same endpoint,
                    same range). */}
                {earnings && earnings.contract != null &&
                  earnings.t.length > 0 && (
                    <PairChart
                      title="Inkomsten & uitgaven"
                      t={earnings.t}
                      bucket={earnings.bucket}
                      aLabel="Verdiend"
                      aValues={earnings.eur_earned}
                      aColor="var(--color-accent, #16a34a)"
                      bLabel="Uitgegeven"
                      bValues={earnings.eur_spent}
                      bColor="var(--color-price-high, #ea580c)"
                      unit="eur"
                    />
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/* ────────────── KPI tile ────────────── */

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint: string;
  tone?: "accent" | "cost";
}) {
  const color =
    tone === "accent" ? "text-accent" :
    tone === "cost"   ? "text-ink"    :
                        "text-ink";
  return (
    <div className="card p-4">
      <div className="text-xs text-ink-mute">{label}</div>
      <div className={`text-2xl font-semibold mt-1 tabular ${color}`}>
        {formatKwh(value)}
      </div>
      <div className="text-xs text-ink-mute mt-0.5">{hint}</div>
    </div>
  );
}

/* ────────────── paired bar chart ────────────── */

const VB_W = 600;
const PAD = { left: 38, right: 8, top: 22, bottom: 22 };

/** 3 'nice' y-ticks (0, mid, top) for a 0-based bar scale. Top is the
 *  data max rounded UP to 1-2 significant digits so gridlines land on
 *  friendly numbers. */
function niceTicks(yMax: number): number[] {
  if (yMax <= 0) return [0];
  const pow = 10 ** Math.floor(Math.log10(yMax));
  const top = Math.ceil((yMax / pow) * 2) / 2 * pow;   // .5 steps of magnitude
  return [0, top / 2, top];
}
function fmtTick(v: number, unit: "kwh" | "eur"): string {
  const s = v >= 10 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, "");
  return unit === "eur" ? `€${s}` : s;
}

interface PairProps {
  title: string;
  t: number[];
  bucket: "hour" | "day" | "month";
  aLabel: string;
  aValues: number[];
  aColor: string;
  bLabel: string;
  bValues: number[];
  bColor: string;
  /** Formats the scrub readout. Default "kwh"; "eur" for the
   *  Inkomsten/Uitgaven chart so € sign and decimals match Prijzen. */
  unit?: "kwh" | "eur";
}

/** Two side-by-side bars per bucket. Bucket bars share a y-scale so
 *  the relative heights between series are honest (a 5 kWh PV bar
 *  next to a 5 kWh Load bar look the same height). Touch-scrub
 *  follows the EarningsHistory pattern - same a11y + iOS-isolation
 *  affordances. */
function PairChart({
  title, t, bucket,
  aLabel, aValues, aColor,
  bLabel, bValues, bColor,
  unit = "kwh",
}: PairProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastVibratedIdx = useRef<number | null>(null);

  const height = 130;
  const innerW = VB_W - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;
  const n = t.length;
  const groupW = innerW / n;

  const dataMax = Math.max(
    0.001,
    ...aValues.map((v) => v || 0),
    ...bValues.map((v) => v || 0),
  );
  const ticks = niceTicks(dataMax);
  const yMax = ticks[ticks.length - 1] || dataMax;
  const yScale = (v: number) => innerH * (v / yMax);

  function pointerToIndex(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vbX = pt.matrixTransform(ctm.inverse()).x;
    const x = Math.max(PAD.left, Math.min(VB_W - PAD.right, vbX));
    const frac = (x - PAD.left) / innerW;
    return Math.min(n - 1, Math.max(0, Math.floor(frac * n)));
  }
  function setFromClient(clientX: number, isTouch: boolean) {
    const idx = pointerToIndex(clientX);
    if (idx === null) return;
    setHoverIdx(idx);
    if (isTouch && idx !== lastVibratedIdx.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibratedIdx.current = idx;
    }
  }
  function clearScrub() {
    setHoverIdx(null);
    lastVibratedIdx.current = null;
  }

  return (
    <section className="card p-4 flex flex-col gap-2">
      <header className="flex items-baseline justify-between text-xs">
        <h3 className="uppercase tracking-wider text-ink-mute">{title}</h3>
        <div className="flex items-center gap-3 text-ink-mute">
          <LegendDot color={aColor} label={aLabel} />
          <LegendDot color={bColor} label={bLabel} />
        </div>
      </header>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${height}`}
        className="w-full select-none"
        style={{ touchAction: "pan-y" }}
        data-swipe-consume="true"
        onMouseMove={(e) => setFromClient(e.clientX, false)}
        onMouseLeave={clearScrub}
        onTouchStart={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchMove={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchEnd={clearScrub}
        onTouchCancel={clearScrub}
      >
        {/* Gridlines + y-scale - bars without a scale don't mean much. */}
        {ticks.map((v) => {
          const y = PAD.top + innerH - yScale(v);
          return (
            <g key={`t${v}`}>
              <line
                x1={PAD.left}
                x2={VB_W - PAD.right}
                y1={y}
                y2={y}
                stroke="var(--color-line, rgba(0,0,0,0.08))"
                strokeWidth={1}
                strokeDasharray={v === 0 ? undefined : "3 4"}
              />
              <text
                x={PAD.left - 6}
                y={y + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--color-ink-mute)"
                className="tabular"
              >
                {fmtTick(v, unit)}
              </text>
            </g>
          );
        })}
        {unit === "kwh" && (
          <text x={PAD.left - 6} y={PAD.top - 8} textAnchor="end" fontSize={8}
            fill="var(--color-ink-mute)">
            kWh
          </text>
        )}

        {t.map((ts, i) => {
          const groupX = PAD.left + i * groupW;
          // Two bars side by side, each 35 % of the group width with
          // a small gap. Bar widths shrink as n grows; capped to a
          // sensible minimum via the group sizing above.
          const barW = groupW * 0.35;
          const aH = yScale(aValues[i] || 0);
          const bH = yScale(bValues[i] || 0);
          const aX = groupX + groupW * 0.12;
          const bX = groupX + groupW * 0.53;
          const baseY = PAD.top + innerH;
          const isHover = i === hoverIdx;
          return (
            <g key={ts} opacity={isHover ? 1 : 0.9}>
              <rect
                x={aX}
                y={baseY - aH}
                width={barW}
                height={Math.max(aH, 1.2)}
                fill={aColor}
                rx={1.5}
              />
              <rect
                x={bX}
                y={baseY - bH}
                width={barW}
                height={Math.max(bH, 1.2)}
                fill={bColor}
                rx={1.5}
              />
            </g>
          );
        })}

        {/* X-axis labels - same ~6-label cap as BucketBars to avoid
            label-soup on a 30-bar view. */}
        {t.map((ts, i) => {
          const label = axisLabelFor(ts, bucket);
          if (!label) return null;
          const step = Math.max(1, Math.floor(n / 6));
          if (i % step !== 0) return null;
          return (
            <text
              key={`l${ts}`}
              x={PAD.left + i * groupW + groupW / 2}
              y={height - 6}
              textAnchor="middle"
              fontSize={10}
              className="fill-ink-mute"
            >
              {label}
            </text>
          );
        })}
      </svg>

      {hoverIdx != null && (
        <div className="text-xs text-ink-mute px-1 flex justify-between gap-2">
          <span>{formatBucketTs(t[hoverIdx], bucket)}</span>
          <span>
            <span style={{ color: aColor }}>
              {unit === "eur"
                ? formatEur(aValues[hoverIdx] || 0)
                : formatKwh(aValues[hoverIdx] || 0)}
            </span>
            {" · "}
            <span style={{ color: bColor }}>
              {unit === "eur"
                ? formatEur(bValues[hoverIdx] || 0)
                : formatKwh(bValues[hoverIdx] || 0)}
            </span>
          </span>
        </div>
      )}
    </section>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span
        className="inline-block w-2 h-2 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

/* ────────────── formatting ────────────── */

function formatKwh(v: number): string {
  // Tile-friendly: 1234 kWh → "1,2 MWh"; otherwise 1 decimal under
  // 10 kWh and integer above (so the digits stay readable on phone).
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(".", ",")} MWh`;
  if (v >= 10)   return `${v.toFixed(0)} kWh`;
  return `${v.toFixed(1).replace(".", ",")} kWh`;
}

function axisLabelFor(unixSec: number, bucket: string): string | null {
  const d = new Date(unixSec * 1000);
  switch (bucket) {
    case "hour":
      return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
    case "day":
      // "wo 3" leest; een kale "3" was raadselachtig (Stefan 10-09).
      return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric" });
    case "month":
      return d.toLocaleDateString("nl-NL", { month: "short" });
    default:
      return null;
  }
}

function formatBucketTs(unixSec: number, bucket: string): string {
  const d = new Date(unixSec * 1000);
  switch (bucket) {
    case "hour":
      return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
    case "day":
      return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
    case "month":
      return d.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
    default:
      return d.toISOString();
  }
}

/* ────────────── saldo per bucket (divergerend) ────────────── */

/** Netto € per bucket rond een nullijn: winst omhoog (groen), kosten
 *  omlaag (oranje). Zelfde scrub-idioom als PairChart. */
function SaldoChart({
  t, bucket, earned, spent,
}: {
  t: number[];
  bucket: "hour" | "day" | "month";
  earned: number[];
  spent: number[];
}) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastVibratedIdx = useRef<number | null>(null);

  const net = t.map((_, i) => (earned[i] || 0) - (spent[i] || 0));
  const height = 130;
  const innerW = VB_W - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;
  const n = t.length;
  const groupW = innerW / n;

  const posMax = Math.max(0.01, ...net.map((v) => Math.max(v, 0)));
  const negMax = Math.max(0, ...net.map((v) => Math.max(-v, 0)));
  const top = niceTicks(posMax).pop() || posMax;
  const bottom = negMax > 0 ? (niceTicks(negMax).pop() || negMax) : 0;
  const span = top + bottom;
  const yOf = (v: number) => PAD.top + ((top - v) / (span || 1)) * innerH;
  const zeroY = yOf(0);

  function pointerToIndex(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vbX = pt.matrixTransform(ctm.inverse()).x;
    const x = Math.max(PAD.left, Math.min(VB_W - PAD.right, vbX));
    return Math.min(n - 1, Math.max(0, Math.floor(((x - PAD.left) / innerW) * n)));
  }
  function setFromClient(clientX: number, isTouch: boolean) {
    const idx = pointerToIndex(clientX);
    if (idx === null) return;
    setHoverIdx(idx);
    if (isTouch && idx !== lastVibratedIdx.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibratedIdx.current = idx;
    }
  }
  function clearScrub() {
    setHoverIdx(null);
    lastVibratedIdx.current = null;
  }

  return (
    <section className="card p-4 flex flex-col gap-2">
      <header className="flex items-baseline justify-between text-xs">
        <h3 className="uppercase tracking-wider text-ink-mute">Saldo per {
          bucket === "hour" ? "uur" : bucket === "day" ? "dag" : "maand"
        }</h3>
        <div className="flex items-center gap-3 text-ink-mute">
          <LegendDot color="var(--color-accent, #16a34a)" label="winst" />
          <LegendDot color="var(--color-price-high, #ea580c)" label="kosten" />
        </div>
      </header>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${height}`}
        className="w-full select-none"
        style={{ touchAction: "pan-y" }}
        data-swipe-consume="true"
        onMouseMove={(e) => setFromClient(e.clientX, false)}
        onMouseLeave={clearScrub}
        onTouchStart={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchMove={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchEnd={clearScrub}
        onTouchCancel={clearScrub}
      >
        {[top, 0, bottom > 0 ? -bottom : null].map((v) =>
          v == null ? null : (
            <g key={`t${v}`}>
              <line
                x1={PAD.left} x2={VB_W - PAD.right} y1={yOf(v)} y2={yOf(v)}
                stroke="var(--color-line, rgba(0,0,0,0.08))" strokeWidth={1}
                strokeDasharray={v === 0 ? undefined : "3 4"}
              />
              <text x={PAD.left - 6} y={yOf(v) + 3} textAnchor="end" fontSize={9}
                fill="var(--color-ink-mute)" className="tabular">
                {fmtTick(Math.abs(v), "eur")}
              </text>
            </g>
          ),
        )}

        {t.map((ts, i) => {
          const v = net[i];
          const barW = groupW * 0.6;
          const x0 = PAD.left + i * groupW + groupW * 0.2;
          const h = Math.max(1.2, (Math.abs(v) / (span || 1)) * innerH);
          const y0 = v >= 0 ? zeroY - h : zeroY;
          return (
            <rect
              key={ts}
              x={x0} y={y0} width={barW} height={h}
              fill={v >= 0 ? "var(--color-accent, #16a34a)" : "var(--color-price-high, #ea580c)"}
              opacity={hoverIdx == null || hoverIdx === i ? 0.95 : 0.55}
              rx={1.5}
            />
          );
        })}

        {t.map((ts, i) => {
          const label = axisLabelFor(ts, bucket);
          if (!label) return null;
          const step = Math.max(1, Math.floor(n / 6));
          if (i % step !== 0) return null;
          return (
            <text key={`l${ts}`} x={PAD.left + i * groupW + groupW / 2}
              y={height - 6} textAnchor="middle" fontSize={10}
              className="fill-ink-mute">
              {label}
            </text>
          );
        })}
      </svg>

      {hoverIdx != null && (
        <div className="text-xs text-ink-mute px-1 flex justify-between gap-2">
          <span>{formatBucketTs(t[hoverIdx], bucket)}</span>
          <span className={net[hoverIdx] >= 0 ? "text-accent" : ""}>
            {formatEur(Math.abs(net[hoverIdx]))}{" "}
            {net[hoverIdx] >= 0 ? "winst" : "kosten"}
          </span>
        </div>
      )}
    </section>
  );
}

